/**
 * AlertRow — tinted-bg + icon container alert.
 * Used for urgent notices, warnings, info messages in page bodies.
 *
 * Usage:
 *   <AlertRow severity="warning" icon={AlertTriangle}
 *     title="3 unclosed days" detail="Daily close needed before today's sale."
 *     action="Close now" onAction={() => navigate('/close-wizard')} />
 */

const TONES = {
  success: {
    bg: '#F0FDF4', border: '#DCFCE7',
    iconBg: '#DCFCE7', iconColor: '#16A34A',
    titleColor: '#166534',
  },
  warning: {
    bg: '#FFFBEB', border: '#FEF3C7',
    iconBg: '#FEF3C7', iconColor: '#D97706',
    titleColor: '#92400E',
  },
  danger: {
    bg: '#FEF2F2', border: '#FEE2E2',
    iconBg: '#FEE2E2', iconColor: '#DC2626',
    titleColor: '#991B1B',
  },
  info: {
    bg: '#EFF6FF', border: '#DBEAFE',
    iconBg: '#DBEAFE', iconColor: '#2563EB',
    titleColor: '#1E40AF',
  },
  neutral: {
    bg: '#F8FAFC', border: '#F1F5F9',
    iconBg: '#F1F5F9', iconColor: '#64748B',
    titleColor: '#475569',
  },
};

export function AlertRow({
  severity = 'info',
  icon: Icon,
  title,
  detail,
  action,
  onAction,
  className = '',
}) {
  const t = TONES[severity] || TONES.neutral;

  return (
    <div
      className={`flex items-start gap-3 rounded-lg px-4 py-3 transition-colors ${className}`}
      style={{
        backgroundColor: t.bg,
        border: `1px solid ${t.border}`,
        transitionDuration: 'var(--ease-fast)',
      }}
    >
      {Icon && (
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded-md flex-shrink-0 mt-0.5"
          style={{ backgroundColor: t.iconBg }}
        >
          <Icon size={15} style={{ color: t.iconColor }} />
        </span>
      )}

      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold leading-snug" style={{ color: t.titleColor }}>
          {title}
        </div>
        {detail && (
          <div className="text-[12px] mt-0.5 leading-snug" style={{ color: 'var(--text-secondary)' }}>
            {detail}
          </div>
        )}
      </div>

      {action && (
        <button
          onClick={onAction}
          className="text-[12px] font-semibold flex-shrink-0 mt-0.5 hover:underline transition-colors"
          style={{ color: 'var(--brand-primary)', transitionDuration: 'var(--ease-fast)' }}
        >
          {action}
        </button>
      )}
    </div>
  );
}
